/* global axios */
import { isOrganizationAccount } from '../../helper/organizationAccessHelper';

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

// Mirrors the old fetch-based error shape (Error with the upstream `detail`
// message) so existing `useAlert(err)` / `err.message` call sites keep working.
const toApiError = err => new Error(err.response?.data?.detail || err.message);

// v2's auto-assign slots only give `schedule_time` + `duration` (minutes) —
// derive an `end_time` so callers built against the v1 `start_time`/`end_time`
// shape don't need to branch on account type themselves.
const addMinutesToTime = (timeStr, minutesToAdd) => {
  const [hh, mm, ss = 0] = timeStr.split(':').map(Number);
  const totalMinutes = hh * 60 + mm + minutesToAdd;
  const newH = Math.floor(totalMinutes / 60) % 24;
  const newM = totalMinutes % 60;
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

export const state = {
  appointmentList: {
    results: [],
    page: 1,
    next: -1,
    total: 0
  },
  branchList: {
    results: [],
    page: 1,
    next: -1,
    total: 0
  },
  serviceList: [],
  selectedBranch: {},
  selectedStaff: null,
  staffList: null,
  scheduleList: null,
  selectedSchedule: null,
  workingHours: null,
  appointmentCount: 0,
  
  // Add filter state properties
  filters: {
    status: null,
    branch_id: null,
    service_id: null,
    start_date: null,
    end_date: null,
  },
  
  // Add pagination state
  pagination: {
    page: 1,
    page_size: 10,
    total_count: 0,
    total_pages: 0,
  },
  
  // Add sorting state
  sorting: {
    sort_by: 'date',
    sort_order: 'asc',
  },

  statusCount: {
    upcoming: 0,
    requested: 0,
    confirmed: 0,
    rescheduled: 0,
    archive: 0
  },

  trackingStatus: "upcoming",

  // service management state
  servicePagination: {
    page: 1,
    pageSize: 20,
    total: 0,
    next: null,
  },
  serviceLoading: false,
  serviceError: null,
}

export const getters = {
  appointmentList(state){
    return state.appointmentList;
  },
  branchList(state){
    return state.branchList;
  },
  selectedBranch(state){
    return state.selectedBranch;
  },
  serviceList(state){
    return state.serviceList;
  },
  selectedStaff(state){
    return state.selectedStaff;
  },
  staffList(state){
    return state.staffList;
  },
  scheduleList(state){
    return state.scheduleList;
  },
  selectedSchedule(state){
    return state.selectedSchedule;
  },
  workingHours(state){
    return state.workingHours;
  },
  appointmentCount(state){
    return state.appointmentCount;
  },
  // New getters for filtering, pagination, and sorting
  filters(state) {
    return state.filters;
  },
  pagination(state) {
    return state.pagination;
  },
  sorting(state) {
    return state.sorting;
  },
  currentPage(state) {
    return state.pagination.page;
  },
  totalPages(state) {
    return state.pagination.total_pages;
  },
  totalCount(state) {
    return state.pagination.total_count;
  },
  pageSize(state) {
    return state.pagination.page_size;
  },
  sortBy(state) {
    return state.sorting.sort_by;
  },
  sortOrder(state) {
    return state.sorting.sort_order;
  },
  statusCount(state) {
    return state.statusCount;
  },
  getTrackingStatus(state){
    return state.trackingStatus;
  }
}

export const actions = {
  async fetchAppointments({ rootGetters, commit, state }, query = { page: 1, page_size: 100 }) {
    const accountId = rootGetters.getCurrentAccountId;
    
    // Combine query with state filters, pagination, and sorting if not explicitly provided
    const combinedQuery = {
      ...query
    };
    
    // If the query doesn't override these parameters, use the state values
    if (!('page' in combinedQuery) && state.pagination.page) {
      combinedQuery.page = state.pagination.page;
    }
    
    if (!('page_size' in combinedQuery) && state.pagination.page_size) {
      combinedQuery.page_size = state.pagination.page_size;
    }
    
    if (!('sort_by' in combinedQuery) && state.sorting.sort_by) {
      combinedQuery.sort_by = state.sorting.sort_by;
    }
    
    if (!('sort_order' in combinedQuery) && state.sorting.sort_order) {
      combinedQuery.sort_order = state.sorting.sort_order;
    }
    
    // Remove null or undefined values
    Object.keys(combinedQuery).forEach(key => 
      (combinedQuery[key] === null || combinedQuery[key] === undefined) && delete combinedQuery[key]
    );
    
    const queryString = new URLSearchParams(combinedQuery).toString();
    
    try {
      const { data: appointments } = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/appointments${queryString ? `?${queryString}` : ''}`)
      );

      // Update state with response
      const appointmentResponse = {
        results: appointments.results || [],
        page: appointments.page || 1,
        next: appointments.next || -1,
        total: appointments.total || 0
      };
      commit('setAppointmentsList', appointmentResponse);
      
      if (!('branch_id' in combinedQuery) && !('service_id' in combinedQuery) && appointments.counts) {
        commit('setStatusCount', appointments.counts);
      }
      
      // Update pagination if total is provided
      if (appointments.total !== undefined) {
        commit('setPagination', {
          total_count: appointments.total,
          total_pages: Math.ceil(appointments.total / (state.pagination.page_size || 10))
        });
      }
      
      return appointments; // Return the data for client-side use
    } catch(err){
      throw toApiError(err);
    }
  },
  
  // New action to update filters and fetch results
  async updateFilters({ commit, dispatch, state }, newFilters) {
    commit('setFilters', newFilters);
    return dispatch('fetchFilteredAppointments');
  },
  
  // New action to handle filtered API requests
  async fetchFilteredAppointments({ state, dispatch }) {
    // Convert state filters to API parameters
    const params = { ...state.filters };
    
    // Add pagination and sorting
    params.page = state.pagination.page;
    params.page_size = state.pagination.page_size;
    params.sort_by = state.sorting.sort_by;
    params.sort_order = state.sorting.sort_order;
    
    // Remove null/undefined values
    Object.keys(params).forEach(key => 
      (params[key] === null || params[key] === undefined) && delete params[key]
    );
    
    return dispatch('fetchAppointments', params);
  },
  
  // New action to update pagination
  async updatePagination({ commit, dispatch }, newPagination) {
    commit('setPagination', newPagination);
    return dispatch('fetchFilteredAppointments');
  },
  
  // New action to update sorting
  async updateSorting({ commit, dispatch }, newSorting) {
    commit('setSorting', newSorting);
    return dispatch('fetchFilteredAppointments');
  },

  // New action to reset filters and pagination, then fetch
  async resetFiltersAndFetch({ commit, dispatch, state }, defaultStatus = 'upcoming') {
    // Reset filters with default status
    commit('resetFilters', defaultStatus);
    
    // Reset pagination to page 1
    commit('setPagination', { 
      page: 1,
      page_size: state.pagination.page_size || 10,
    });
    
    // Fetch with the reset state
    return dispatch('fetchFilteredAppointments');
  },

  async updateAppointmentCount({ rootGetters, commit }) {
    const accountId = rootGetters.getCurrentAccountId;
    try{
      const { data: appointments } = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/appointments?status=requested`)
      );
      const count = appointments.counts?.requested ?? appointments.total ?? 0;
      commit('setAppointmentCount', count);
      return count;
    } catch(err){
      throw toApiError(err);
    }
  },

  async fetchAvailableDates({ rootGetters }, query = {}) {
    const accountId = rootGetters.getCurrentAccountId;
    const queryString = new URLSearchParams(query).toString();
    const isOrganization = isOrganizationAccount({ getters: rootGetters }, accountId);
    const path = isOrganization
      ? `/v2/accounts/${accountId}/availability/dates`
      : `/account_id/${accountId}/available_dates`;
    try{
      const { data } = await axios.get(
        knowledgeProxy(accountId, `${path}${queryString ? `?${queryString}` : ''}`)
      );
      // v1 returns a bare array of date strings; v2 wraps it as { available_dates }.
      return isOrganization ? data : { available_dates: data };
    } catch(err){
      throw toApiError(err);
    }
  },

  async fetchAvailableTimes({ rootGetters }, query = {}) {
    const accountId = rootGetters.getCurrentAccountId;
    const queryString = new URLSearchParams(query).toString();
    const isOrganization = isOrganizationAccount({ getters: rootGetters }, accountId);
    const path = isOrganization
      ? `/v2/accounts/${accountId}/availability/times`
      : `/account_id/${accountId}/available_times`;
    try{
      const { data } = await axios.get(
        knowledgeProxy(accountId, `${path}${queryString ? `?${queryString}` : ''}`)
      );
      // v1 returns a bare array of slot objects; v2 wraps it as { time_slots, schedule_timezone }.
      return isOrganization
        ? data
        : { time_slots: data, schedule_timezone: data[0]?.schedule_timezone };
    } catch(err){
      throw toApiError(err);
    }
  },

  async fetchAppointment({ rootGetters }, appointmentId){
    const accountId = rootGetters.getCurrentAccountId;
    try{
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/appointments/${appointmentId}`)
      );
      return data;
    } catch(err){
      throw toApiError(err);
    }
  },

  async createAppointment({ rootGetters }, payload){
    try{
      const accountId = rootGetters.getCurrentAccountId;
      const path = isOrganizationAccount({ getters: rootGetters }, accountId)
        ? `/v2/accounts/${accountId}/appointments`
        : `/accounts/${accountId}/appointments`;
      await axios.post(
        knowledgeProxy(accountId, path),
        { ...payload }
      );
    }catch(err){
      throw toApiError(err);
    }
  },

  async createAutoAssignTimeSlots({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      if (isOrganizationAccount({ getters: rootGetters }, accountId)) {
        const { branch_id, service_id, date, client_timezone } = payload;
        const query = { branch_id, service_id, date };
        if (client_timezone) query.client_timezone = client_timezone;
        const queryString = new URLSearchParams(query).toString();
        const { data } = await axios.get(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/availability/times/auto-assign?${queryString}`)
        );
        return (data.time_slots || []).map(slot => ({
          start_time: slot.schedule_time,
          end_time: addMinutesToTime(slot.schedule_time, slot.duration),
          available_staff_count: slot.available_staff_ids?.length,
        }));
      }

      const { data } = await axios.post(
        knowledgeProxy(accountId, `/accounts/${accountId}/auto_assign_time_slots`),
        { ...payload }
      );
      return data.available_time_slots;
    } catch (err) {
      throw toApiError(err);
    }
  },

  async createAutoAssignAppointment({ rootGetters }, payload) {
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const path = isOrganizationAccount({ getters: rootGetters }, accountId)
        ? `/v2/accounts/${accountId}/appointments/auto-assign`
        : `/account_id/${accountId}/auto_assign_appointment`;
      const { data } = await axios.post(
        knowledgeProxy(accountId, path),
        payload
      );
      return data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || err.message || 'Failed to create appointment');
    }
  },

  async deleteAppointment({rootGetters}, appointmentID){
    try{
      const accountId = rootGetters.getCurrentAccountId;
      await axios.delete(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/appointments/${appointmentID}`)
      );
    }catch(err){
      throw toApiError(err);
    }
  },

  async rescheduleAppointment({rootGetters}, {appointmentId, payload}){
    try{
      const accountId = rootGetters.getCurrentAccountId;
      await axios.put(
        knowledgeProxy(accountId, `/accounts/${accountId}/appointments/${appointmentId}/reschedule`),
        payload
      );
    }catch(err){
      throw toApiError(err);
    }
  },

  async updateAppointmentStatus({rootGetters, dispatch}, {appointmentId, status}){
    try{
      const accountId = rootGetters.getCurrentAccountId;
      await axios.put(
        knowledgeProxy(accountId, `/accounts/${accountId}/appointments/${appointmentId}/status?status=${status}`)
      );

      // Refresh appointments list to update state
      await dispatch('fetchFilteredAppointments');
    }catch(err){
      throw toApiError(err);
    }
  },

  async fetchBranches({ rootGetters, commit }, { page = 1, page_size = 10 } = {}) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches`),
        { params: { page, page_size } }
      );
      commit('setBranchList', res.data);
      return res.data;
    } catch (error) {
      console.error('Fetch branches failed:', error);
      throw error.response?.data || error;
    }
  },

  async createBranch({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Create branch failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchBranch({ rootGetters, commit }, branchId) {
    if (branchId === null || branchId.length === 0) {
      throw new Error('Branch ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches/${branchId}`)
      );
      commit('setSelectedBranch', res.data);
      return res.data;
    } catch (error) {
      console.error('Fetch branch failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteBranch({ rootGetters }, branchId) {
    if (!branchId) {
      throw new Error('Branch ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches/${branchId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete branch failed:', error);
      throw error.response?.data || error;
    }
  },

  async editBranch({ rootGetters }, { branchId, payload }) {
    if (branchId === null || branchId.length === 0) {
      throw new Error('Branch ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches/${branchId}`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Edit branch failed:', error);
      throw error.response?.data || error;
    }
  },

  async createService({ rootGetters }, { branchId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;

    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => formData.append(key, value));

    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches/${branchId}/services`),
        formData
      );
      return res.data;
    } catch (error) {
      console.error('Create service failed:', error);
      throw error.response?.data || error;
    }
  },

  async editService({ rootGetters }, { serviceId, payload }) {
    if (!serviceId) throw new Error('Service ID is required');

    const accountId = rootGetters.getCurrentAccountId;

    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => formData.append(key, value));

    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/accounts/${accountId}/services/${serviceId}`),
        formData
      );
      return res.data;
    } catch (error) {
      console.error('Edit service failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchServices({ rootGetters, commit }, branchId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches/${branchId}/services`)
      );
      commit('setServiceList', res.data);
      return res.data;
    } catch (error) {
      console.error('Fetch services failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchServicesV2({ rootGetters, commit, state }, branchId) {
    const accountId = rootGetters.getCurrentAccountId;
    commit('setServiceLoading', true);
    commit('setServiceError', null);

    try {
      const params = {
        page: state.servicePagination.page,
        page_size: state.servicePagination.pageSize,
      };

      const res = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches/${branchId}/services`),
        { params }
      );

      commit('setServiceList', res.data);
      commit('setServicePagination', {
        page: res.data.page,
        total: res.data.total,
        next: res.data.next,
      });

      return res.data;
    } catch (error) {
      console.error('Fetch services v2 failed:', error);
      commit('setServiceError', error.response?.data?.detail || error.message);
      throw error.response?.data || error;
    } finally {
      commit('setServiceLoading', false);
    }
  },

  async nextServicePage({ state, commit, dispatch }, branchId) {
    if (!state.servicePagination.next || state.servicePagination.next === -1) return;

    commit("setServicePage", state.servicePagination.next);
    await dispatch("fetchServicesV2", branchId);
  },

  async prevServicePage({ state, commit, dispatch }, branchId) {
    if (state.servicePagination.page <= 1) return;

    commit("setServicePage", state.servicePagination.page - 1);
    await dispatch("fetchServicesV2", branchId);
  },

  async jumpToServicePage({ commit, dispatch }, { branchId, page }) {
    commit("setServicePage", page);
    await dispatch("fetchServicesV2", branchId);
  },

  async changeServicePageSize({ commit, dispatch }, { branchId, size }) {
    commit("setServicePageSize", size);
    commit("setServicePage", 1); // reset to top
    await dispatch("fetchServicesV2", branchId);
  },
  
  async fetchService({ rootGetters, commit }, serviceId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      if (isOrganizationAccount({ getters: rootGetters }, accountId)) {
        // The v1 service detail returns staff: [] for organization accounts. v2
        // lists them as { id, name }; map to the v1 shape the reassign modal reads.
        const res = await axios.get(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/services/${serviceId}`)
        );
        return {
          ...res.data,
          staff: (res.data.staff || []).map(staff => ({
            staff_id: staff.id,
            staff_name: staff.name,
          })),
        };
      }
      const res = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/services/${serviceId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Fetch service failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteService({ rootGetters }, serviceId) {
    if (serviceId === null || serviceId.length === 0) {
      throw new Error('Service ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/accounts/${accountId}/services/${serviceId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete service failed:', error);
      throw error.response?.data || error;
    }
  },

  async createStaff({ rootGetters }, { branchId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches/${branchId}/staff`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Create staff failed:', error);
      throw error.response?.data || error;
    }
  },

  async editStaff({ rootGetters }, { staffId, payload }) {
    if (staffId === null || staffId.length === 0) {
      throw new Error('Staff ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/accounts/${accountId}/staff/${staffId}`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Edit staff failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchStaff({ rootGetters, commit }, staffId) {
    if (staffId === null || staffId.length === 0) {
      throw new Error('Staff ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/staff/${staffId}`)
      );
      commit('setSelectedStaff', res.data);
      return res.data;
    } catch (error) {
      console.error('Fetch staff failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchStaffList({ rootGetters, commit }, branchId) {
    if (!branchId) {
      throw new Error('Branch ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches/${branchId}/staff`)
      );
      commit('setStaffList', res.data);
      return res.data;
    } catch (error) {
      console.error('Fetch staff list failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteStaff({ rootGetters }, staffId) {
    if (staffId === null || staffId.length === 0) {
      throw new Error('Staff ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/accounts/${accountId}/staff/${staffId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete staff failed:', error);
      throw error.response?.data || error;
    }
  },

  async createSchedule({ rootGetters }, { branchId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/branches/${branchId}/schedules`),
        payload
      );
      return res.data.schedule_id;
    } catch (error) {
      console.error('Create schedule failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchScheduleList({ rootGetters, commit }, branchId) {
    if (branchId === null || branchId.length === 0) {
      throw new Error('Branch ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/branches/${branchId}/schedules`)
      );
      commit('setScheduleList', res.data);
      return res.data;
    } catch (error) {
      console.error('Fetch schedule list failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchSchedule({ rootGetters, commit }, scheduleId) {
    if (scheduleId === null || scheduleId.length === 0) {
      throw new Error('Schedule ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/schedules/${scheduleId}`)
      );
      commit('setSelectedSchedule', res.data);
      return res.data;
    } catch (error) {
      console.error('Fetch schedule failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteSchedule({ rootGetters }, scheduleId) {
    if (scheduleId === null || scheduleId.length === 0) {
      throw new Error('Schedule ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/schedules/${scheduleId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete schedule failed:', error);
      throw error.response?.data || error;
    }
  },

  async editSchedule({ rootGetters }, { scheduleId, payload }) {
    if (scheduleId === null || scheduleId.length === 0) {
      throw new Error('Schedule ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/schedules/${scheduleId}`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Edit schedule failed:', error);
      throw error.response?.data || error;
    }
  },

  async createWorkingHours({ rootGetters }, { scheduleId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/schedules/${scheduleId}/working-hours`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Create working hours failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchWorkingHours({ rootGetters }, scheduleId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/schedules/${scheduleId}/working-hours`)
      );
      return res.data;
    } catch (error) {
      console.error('Fetch working hours failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteWorkingHours({ rootGetters }, { scheduleId, workingHoursId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/schedules/${scheduleId}/working-hours/${workingHoursId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete working hours failed:', error);
      throw error.response?.data || error;
    }
  },

  async editWorkingHours({ rootGetters }, { scheduleId, workingHoursId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/schedules/${scheduleId}/working-hours/${workingHoursId}`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Edit working hours failed:', error);
      throw error.response?.data || error;
    }
  },

  async toggleSchedule({ rootGetters }, { branchId, scheduleId, toggle }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/branches/${branchId}/schedules/${scheduleId}/${toggle}`)
      );
      return res.data;
    } catch (error) {
      console.error('Toggle schedule failed:', error);
      throw error.response?.data || error;
    }
  },

  async assignStaffService({ rootGetters }, { staffId, payload }) {
    if (staffId === null || staffId.length === 0) {
      throw new Error('Staff ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/accounts/${accountId}/staff/${staffId}/services`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Assign staff service failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchStaffServices({ rootGetters, commit }, { staffId, page = 1, page_size = 10 }) {
    if (staffId === null || staffId.length === 0) {
      throw new Error('Staff ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/staff/${staffId}/services`),
        { params: { page, page_size } }
      );
      return res.data;
    } catch (error) {
      console.error('Fetch staff services failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteStaffService({ rootGetters, commit }, { staffId, serviceId }) {
    if (staffId === null || staffId.length === 0) {
      throw new Error('Staff ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/accounts/${accountId}/staff/${staffId}/services/${serviceId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete staff service failed:', error);
      throw error.response?.data || error;
    }
  },

  async scheduleOverride({rootGetters}, {scheduleId, payload}){
    try{
      const accountId = rootGetters.getCurrentAccountId;
      await axios.post(
        knowledgeProxy(accountId, `/schedules/${scheduleId}/overrides`),
        payload
      );
    }catch(err){
      throw toApiError(err);
    }
  },

  async fetchScheduleOverrides({rootGetters} , scheduleId){
    try{
      const accountId = rootGetters.getCurrentAccountId;
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/schedules/${scheduleId}/overrides`)
      );
      return data;
    }catch(err){
      throw toApiError(err);
    }
  },

  async deleteScheduleOverride({rootGetters}, {overrideId, scheduleId}){
    try{
      const accountId = rootGetters.getCurrentAccountId;
      await axios.delete(
        knowledgeProxy(accountId, `/schedules/${scheduleId}/overrides/${overrideId}`)
      );
    }catch(err){
      throw toApiError(err);
    }
  },

  async assignStaffSchedule({ rootGetters }, { staffId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/accounts/${accountId}/staff/${staffId}/schedule`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Assign staff schedule failed:', error);
      throw error.response?.data || error;
    }
  },

  async removeStaffSchedule({ rootGetters }, { staffId, scheduleId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/accounts/${accountId}/staff/${staffId}/schedules/${scheduleId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Remove staff schedule failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchStaffUnavailability({ rootGetters }, staffId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/staff/${staffId}/unavailability`)
      );
      return res.data;
    } catch (error) {
      console.error('Fetch staff unavailability failed:', error);
      throw error.response?.data || error;
    }
  },

  async createStaffUnavailability({ rootGetters }, { staffId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/accounts/${accountId}/staff/${staffId}/unavailability_items`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Create staff unavailability failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteStaffUnavailability({ rootGetters }, { staffId, date }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/accounts/${accountId}/staff/${staffId}/unavailability/${date}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete staff unavailability failed:', error);
      throw error.response?.data || error;
    }
  },

  async reassignStaffAppointment({ rootGetters }, { appointmentId, payload }) {
    if (!appointmentId) {
      throw new Error('Appointment ID is required');
    }
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/accounts/${accountId}/appointments/${appointmentId}/reassign`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Reassign staff appointment failed:', error);
      throw error.response?.data || { detail: 'Failed to reassign staff.' };
    }
  },

  async deleteStaffSchedule({ rootGetters }, { staffId, scheduleId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/accounts/${accountId}/staff/${staffId}/schedules/${scheduleId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete staff schedule failed:', error);
      throw error.response?.data || { detail: 'Failed to delete staff schedule.' };
    }
  },

  async getTimezones({ rootGetters }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/timezones`)
      );
      return res.data;
    } catch (error) {
      console.error('Get timezones failed:', error);
      throw error.response?.data || error;
    }
  },
  
  updateTrackingStatus({commit}, status){
    localStorage.setItem('trackStatus', status);
    commit('setTrackingStatus', status);
    commit('fetchTrackingStatus');
  },

  fetchTrackingStatus({commit}){
    commit('setTrackingStatus', localStorage.getItem('trackStatus') ? localStorage.getItem('trackStatus') : 'upcoming');
  }
}

export const mutations = {
  setAppointmentsList(state, appointmentList){
    state.appointmentList = appointmentList;
  },
  setBranchList(state, branchList){
    state.branchList = branchList;
  },
  setSelectedBranch(state, selectedBranch){
    state.selectedBranch = selectedBranch;
  },
  setSelectedStaff(state, selectedStaff){
    state.selectedStaff = selectedStaff;
  },
  setServiceList(state, serviceList){
    state.serviceList = serviceList;
  },
  setStaffList(state, staffList){
    state.staffList = staffList;
  },
  setScheduleList(state, scheduleList){
    state.scheduleList = scheduleList;
  },
  setSelectedSchedule(state, selectedSchedule){ 
    state.selectedSchedule = selectedSchedule;
  },
  setWorkingHours(state, workingHours){
    state.workingHours = workingHours;
  },
  setAppointmentCount(state, appointmentCount){ 
    state.appointmentCount = appointmentCount;
  },
  // New mutations for filters, pagination, and sorting
  setFilters(state, filters) {
    state.filters = { ...state.filters, ...filters };
  },
  resetFilters(state, defaultStatus = 'upcoming') {
    state.filters = {
      status: defaultStatus,
      branch_id: null,
      service_id: null,
      start_date: null,
      end_date: null,
    };
  },
  setPagination(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination };
  },
  setSorting(state, sorting) {
    state.sorting = { ...state.sorting, ...sorting };
  },
  setStatusCount(state, statusCount) {
    state.statusCount = statusCount;
  },
  setTrackingStatus(state, status){
    state.trackingStatus = status;
  },

  setServicePagination(state, meta) {
    state.servicePagination.page = meta.page;
    state.servicePagination.total = meta.total;
    state.servicePagination.next = meta.next;
  },
  setServicePage(state, page) {
    state.servicePagination.page = page;
  },
  setServicePageSize(state, size) {
    state.servicePagination.pageSize = size;
  },
  setServiceLoading(state, value) {
    state.serviceLoading = value;
  },
  setServiceError(state, error) {
    state.serviceError = error;
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};